import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import PlayerAvatar from "@/components/app/PlayerAvatar";
import { Button } from "@/components/ui/button";
import { Share2, Download, Trophy, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import QRCode from "qrcode";

const LOGO_URL = "/nphs-logo.png";

export default function BoxScore() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get(`/games/${id}/box-score`).then((r) => setData(r.data)).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = window.location.href;
    QRCode.toDataURL(url, {
      width: 360,
      margin: 1,
      errorCorrectionLevel: "M",
      color: { dark: "#0A0A0A", light: "#FFFFFF" },
    }).then(setQrDataUrl).catch(() => setQrDataUrl(null));
  }, [id]);

  const share = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied!");
    } catch { toast.error("Copy failed"); }
  };

  const shareCard = async () => {
    if (!cardRef.current) return;
    try {
      const canvas = await html2canvas(cardRef.current, { backgroundColor: "#0A0A0A", scale: 2, useCORS: true, logging: false });
      // download
      const dataUrl = canvas.toDataURL("image/png");
      const a = document.createElement("a");
      a.href = dataUrl;
      a.download = `nphs-${data.game.opponent.replace(/\s+/g, "-")}-${data.game.date}.png`;
      a.click();
      // copy to clipboard
      try {
        const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
        if (blob && navigator.clipboard?.write) {
          await navigator.clipboard.write([new ClipboardItem({ "image/png": blob })]);
          toast.success("Card downloaded + copied to clipboard");
          return;
        }
      } catch { /* clipboard image not supported */ }
      toast.success("Card downloaded");
    } catch (e) {
      toast.error("Couldn't generate card");
    }
  };

  if (!data) return <div className="p-10 text-ink-300">Loading box score…</div>;
  const { game, lines, totals, top_scorer } = data;
  const result = game.nphs_score !== null && game.opp_score !== null
    ? (game.nphs_score > game.opp_score ? "W" : "L") : null;

  return (
    <div className="p-6 md:p-10">
      <Link to="/schedule" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-ink-300 hover:text-white mb-4">
        <ArrowLeft className="w-3 h-3" /> Schedule
      </Link>

      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="eyebrow">{game.playoff ? "Playoff Game" : "Regular Season"}</p>
          <h1 className="heading-1 leading-none">
            {game.home_away === "home" ? "vs" : "@"} <span className="text-burgundy">{game.opponent}</span>
          </h1>
          <p className="text-ink-300 mt-2">{game.date}{game.location ? ` · ${game.location}` : ""}</p>
        </div>
        <div className="text-right">
          {result && (
            <p className="eyebrow text-[10px]">
              <span className={result === "W" ? "text-green-400" : "text-red-400"}>{result === "W" ? "Win" : "Loss"}</span>
            </p>
          )}
          <p className="font-display text-5xl font-black">
            {game.nphs_score ?? "—"}<span className="text-ink-500"> - </span>{game.opp_score ?? "—"}
          </p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        <Button onClick={shareCard} className="bg-burgundy hover:bg-burgundy-hover" data-testid="boxscore-share-card-btn">
          <Download className="w-4 h-4 mr-2" /> Share Card
        </Button>
        <Button onClick={share} variant="outline" className="border-ink-700 hover:bg-ink-800" data-testid="boxscore-copy-link-btn">
          <Share2 className="w-4 h-4 mr-2" /> Copy Link
        </Button>
      </div>

      <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-700/60 flex items-center justify-between">
          <h3 className="heading-3">Box Score</h3>
          {top_scorer && (
            <p className="text-xs uppercase tracking-wider text-ink-300">
              <Trophy className="w-3.5 h-3.5 inline text-burgundy mr-1" />
              Top: <span className="text-white font-bold">{top_scorer.player.player_name}</span> · {top_scorer.points} pts
            </p>
          )}
        </div>
        {lines.length === 0 ? (
          <p className="text-center text-ink-400 py-10">No stats recorded for this game yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-ink-900 text-ink-300">
                <tr className="text-left">
                  <th className="px-4 py-3 uppercase text-xs tracking-wider sticky left-0 bg-ink-900">Player</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">MIN</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">FG</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">3PT</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">FT</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">REB</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">AST</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">STL</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">BLK</th>
                  <th className="px-2 py-3 text-center uppercase text-xs">TO</th>
                  <th className="px-2 py-3 text-center uppercase text-xs bg-burgundy/30 text-white">PTS</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.player.id} className="border-t border-ink-700/40 hover:bg-ink-900/40">
                    <td className="px-4 py-2 sticky left-0 bg-ink-800">
                      <Link to={`/player/${l.player.slug}`} className="flex items-center gap-3 min-w-[180px] hover:text-burgundy">
                        <PlayerAvatar player={l.player} size="sm" />
                        <span className="font-semibold">{l.player.player_name}</span>
                      </Link>
                    </td>
                    <td className="px-2 py-2 text-center">{l.minutes || "—"}</td>
                    <td className="px-2 py-2 text-center">{l.fg2_made + l.fg3_made}-{l.fg2_att + l.fg3_att}</td>
                    <td className="px-2 py-2 text-center">{l.fg3_made}-{l.fg3_att}</td>
                    <td className="px-2 py-2 text-center">{l.ft_made}-{l.ft_att}</td>
                    <td className="px-2 py-2 text-center">{l.rebounds}</td>
                    <td className="px-2 py-2 text-center">{l.assists}</td>
                    <td className="px-2 py-2 text-center">{l.steals}</td>
                    <td className="px-2 py-2 text-center">{l.blocks}</td>
                    <td className="px-2 py-2 text-center">{l.turnovers}</td>
                    <td className="px-2 py-2 text-center font-display font-bold text-burgundy">{l.points}</td>
                  </tr>
                ))}
                <tr className="border-t-2 border-burgundy/60 bg-ink-900/60 font-bold">
                  <td className="px-4 py-3 sticky left-0 bg-ink-900/60 uppercase font-display tracking-wider">Totals</td>
                  <td />
                  <td className="px-2 py-3 text-center">{totals.fg2_made + totals.fg3_made}-{totals.fg2_att + totals.fg3_att}</td>
                  <td className="px-2 py-3 text-center">{totals.fg3_made}-{totals.fg3_att}</td>
                  <td className="px-2 py-3 text-center">{totals.ft_made}-{totals.ft_att}</td>
                  <td className="px-2 py-3 text-center">{totals.rebounds}</td>
                  <td className="px-2 py-3 text-center">{totals.assists}</td>
                  <td className="px-2 py-3 text-center">{totals.steals}</td>
                  <td className="px-2 py-3 text-center">{totals.blocks}</td>
                  <td className="px-2 py-3 text-center">{totals.turnovers}</td>
                  <td className="px-2 py-3 text-center font-display text-burgundy text-lg">{totals.points}</td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Off-screen share card template — rendered via html2canvas */}
      <div className="fixed -left-[9999px] top-0 pointer-events-none" aria-hidden>
        <div
          ref={cardRef}
          style={{
            width: "1080px", height: "1080px",
            background: "linear-gradient(135deg, #0A0A0A 0%, #1C1C21 60%, #4A0A12 100%)",
            color: "white", fontFamily: "'Barlow Condensed', sans-serif", padding: "72px", position: "relative",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <img src={LOGO_URL} alt="" style={{ width: 110, height: 110, borderRadius: "50%", border: "3px solid #6B0F1A", background: "#000" }} />
            <div>
              <p style={{ margin: 0, fontSize: 18, letterSpacing: "0.2em", color: "#A1A1AA", textTransform: "uppercase" }}>North Plainfield High School</p>
              <p style={{ margin: 0, fontSize: 48, fontWeight: 900, letterSpacing: "0.02em", textTransform: "uppercase" }}>NPHS <span style={{ color: "#6B0F1A" }}>Canucks</span></p>
            </div>
          </div>

          <p style={{ marginTop: 64, fontSize: 26, letterSpacing: "0.2em", color: "#A1A1AA", textTransform: "uppercase" }}>
            {game.playoff ? "Playoff" : "Game Recap"} · {game.date}
          </p>
          <p style={{ margin: "8px 0 0 0", fontSize: 92, fontWeight: 900, lineHeight: 1, textTransform: "uppercase" }}>
            {game.home_away === "home" ? "vs" : "@"} <span style={{ color: "#6B0F1A" }}>{game.opponent}</span>
          </p>

          {result && (
            <div style={{ marginTop: 56, display: "flex", alignItems: "flex-end", gap: 32 }}>
              <span style={{ fontSize: 220, fontWeight: 900, lineHeight: 1, color: result === "W" ? "#22C55E" : "#EF4444" }}>{result}</span>
              <span style={{ fontSize: 180, fontWeight: 900, lineHeight: 1 }}>{game.nphs_score}<span style={{ color: "#71717A" }}> - </span>{game.opp_score}</span>
            </div>
          )}

          {top_scorer && (
            <div style={{ marginTop: 64, padding: "32px 36px", background: "rgba(107,15,26,0.25)", border: "2px solid rgba(107,15,26,0.5)", borderRadius: 16 }}>
              <p style={{ margin: 0, fontSize: 20, letterSpacing: "0.25em", color: "#A1A1AA", textTransform: "uppercase" }}>Top Performer</p>
              <p style={{ margin: "6px 0 0 0", fontSize: 56, fontWeight: 800, textTransform: "uppercase" }}>{top_scorer.player.player_name}</p>
              <div style={{ marginTop: 18, display: "flex", gap: 40 }}>
                <Stat val={top_scorer.points} label="PTS" hi />
                <Stat val={top_scorer.rebounds} label="REB" />
                <Stat val={top_scorer.assists} label="AST" />
                <Stat val={top_scorer.steals} label="STL" />
              </div>
            </div>
          )}

          <p style={{ position: "absolute", bottom: 56, left: 72, fontSize: 16, color: "#52525B", letterSpacing: "0.2em", textTransform: "uppercase" }}>
            Coach Derrick Canada · #GoCanucks
          </p>

          {qrDataUrl && (
            <div style={{ position: "absolute", bottom: 56, right: 56, background: "white", padding: 14, borderRadius: 12, display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
              <img src={qrDataUrl} alt="QR" style={{ width: 160, height: 160, display: "block" }} />
              <p style={{ margin: 0, fontSize: 11, letterSpacing: "0.18em", color: "#0A0A0A", textTransform: "uppercase", fontWeight: 700 }}>Scan for box score</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ val, label, hi }) {
  return (
    <div>
      <p style={{ margin: 0, fontSize: 64, fontWeight: 900, lineHeight: 1, color: hi ? "#6B0F1A" : "white" }}>{val}</p>
      <p style={{ margin: "4px 0 0 0", fontSize: 16, letterSpacing: "0.2em", color: "#A1A1AA", textTransform: "uppercase" }}>{label}</p>
    </div>
  );
}
